import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../../contexts/AuthContext";
import {
  AcademicCapIcon,
  UserGroupIcon,
  VideoCameraIcon,
  DocumentTextIcon,
  StarIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
  EyeIcon,
  EyeSlashIcon,
  BookOpenIcon,
  LightBulbIcon,
  ChartBarIcon,
  ClockIcon,
  CheckCircleIcon,
  HeartIcon,
  TrophyIcon,
  FireIcon,
  SparklesIcon,
  GlobeAltIcon,
  CogIcon,
  ShieldCheckIcon,
  HomeIcon,
  UserIcon,
  Cog6ToothIcon,
  BellIcon,
  MagnifyingGlassIcon,
  ChatBubbleLeftIcon,
  CameraIcon,
  MusicalNoteIcon,
  GiftIcon,
  ShoppingBagIcon,
  CreditCardIcon,
  TruckIcon,
  WrenchScrewdriverIcon,
  PaintBrushIcon,
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
  SpeakerWaveIcon,
  MicrophoneIcon,
  VideoCameraSlashIcon,
  PhotoIcon,
  FilmIcon,
  PlayIcon,
  PauseIcon,
  StopIcon,
  ForwardIcon,
  BackwardIcon,
  WifiIcon,
  SignalIcon,
  SunIcon,
  MoonIcon,
  CloudIcon,
  BoltIcon,
  BanknotesIcon,
  CurrencyDollarIcon,
  ReceiptRefundIcon,
  ShoppingCartIcon,
  TagIcon,
  TicketIcon,
  ReceiptPercentIcon,
  CalculatorIcon,
  ChartPieIcon,
  PresentationChartLineIcon,
  DocumentChartBarIcon,
  ClipboardDocumentListIcon,
  ClipboardDocumentCheckIcon,
  ClipboardIcon,
  DocumentIcon,
  DocumentDuplicateIcon,
  FolderIcon,
  FolderOpenIcon,
  FolderPlusIcon,
  PaperAirplaneIcon,
  PaperClipIcon,
  DocumentArrowDownIcon,
  DocumentArrowUpIcon,
  DocumentMagnifyingGlassIcon,
  DocumentPlusIcon,
  DocumentMinusIcon,
  DocumentCheckIcon,
  ShareIcon,
  LinkIcon,
  AtSymbolIcon,
  HashtagIcon,
} from "@heroicons/react/24/outline";
import api, { apiUpload } from "../../services/api";
import { refreshBranding } from "../../services/siteBrandingService";

interface LandingPageContent {
  sections: any[];
  features: any[];
  testimonials: any[];
  faqs: any[];
  settings: any[];
  logos: any[];
  stats: any[];
  socialMedia: any[];
}

const LandingPage: React.FC = () => {
  const { user } = useAuth();
  const [content, setContent] = useState<LandingPageContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<
    | "sections"
    | "features"
    | "testimonials"
    | "faqs"
    | "settings"
    | "logos"
    | "stats"
    | "socialMedia"
  >("sections");
  const [editingItem, setEditingItem] = useState<any>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<any>({});
  const [showIconSelector, setShowIconSelector] = useState(false);
  const [iconSelectorField, setIconSelectorField] = useState<string>("");
  const [uploadingImage, setUploadingImage] = useState(false);
  const [currentImageField, setCurrentImageField] = useState<"logo" | "avatar">(
    "logo",
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{
    id: number;
    label: string;
    endpoint: string;
  } | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      setLoading(true);
      const response = await api.get("/landing-page/admin/content");
      setContent(response.data);
    } catch (err: any) {
      console.error("Error fetching content:", err);
      setError("Failed to load content");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      let endpoint;
      let requestData;

      if (activeTab === "sections") {
        const sectionName = editingItem?.section_name || editingItem?.id;
        endpoint = `/landing-page/admin/${activeTab}/${sectionName}`;
        requestData = {
          title: formData.title,
          subtitle: formData.subtitle,
          content: formData.content,
          button_text: formData.button_text,
          button_link: formData.button_link,
          is_active: formData.is_active,
        };
      } else if (activeTab === "settings") {
        const settingKey = editingItem?.setting_key || editingItem?.id;
        endpoint = `/landing-page/admin/${activeTab}/${settingKey}`;
        requestData = { value: formData.value };

        // Refresh branding if site title or favicon is updated
        if (settingKey === "site_title" || settingKey === "site_favicon") {
          await refreshBranding();
        }
      } else if (activeTab === "features") {
        endpoint = `/landing-page/admin/${activeTab}/${editingItem.id}`;
        requestData = {
          title: formData.title,
          description: formData.description,
          icon_name: formData.icon_name,
          link_text: formData.link_text,
          link_url: formData.link_url,
          sort_order: formData.sort_order || 0,
          is_active: formData.is_active,
        };
      } else if (activeTab === "testimonials") {
        endpoint = `/landing-page/admin/${activeTab}/${editingItem.id}`;
        requestData = {
          name: formData.name,
          role: formData.role,
          content: formData.content,
          avatar_url: formData.avatar_url,
          rating: formData.rating || 5,
          sort_order: formData.sort_order || 0,
          is_active: formData.is_active,
        };
      } else if (activeTab === "faqs") {
        endpoint = `/landing-page/admin/${activeTab}/${editingItem.id}`;
        requestData = {
          question: formData.question,
          answer: formData.answer,
          sort_order: formData.sort_order || 0,
          is_active: formData.is_active,
        };
      } else if (activeTab === "logos") {
        endpoint = `/landing-page/admin/${activeTab}/${editingItem.id}`;
        requestData = {
          logo_type: formData.logo_type,
          logo_url: formData.logo_url,
          logo_alt_text: formData.logo_alt_text,
          sort_order: formData.sort_order || 0,
          is_active: formData.is_active,
        };
      } else if (activeTab === "stats") {
        endpoint = `/landing-page/admin/${activeTab}/${editingItem.id}`;
        requestData = {
          stat_title: formData.stat_title,
          stat_value: formData.stat_value,
          stat_description: formData.stat_description,
          stat_icon: formData.stat_icon,
          sort_order: formData.sort_order || 0,
          is_active: formData.is_active,
        };
      } else if (activeTab === "socialMedia") {
        endpoint = `/landing-page/admin/social-media/${editingItem.id}`;
        requestData = {
          platform_name: formData.platform_name,
          platform_icon: formData.platform_icon,
          profile_url: formData.profile_url,
          display_name: formData.display_name,
          sort_order: formData.sort_order || 0,
          is_active: formData.is_active,
        };
      }

      if (!endpoint) {
        throw new Error("Invalid endpoint");
      }

      await api.put(endpoint, requestData);
      await fetchContent();
      setShowEditModal(false);
      setEditingItem(null);
      setFormData({});
    } catch (err: any) {
      console.error("Error saving:", err);
      setError("ການບັນທຶກການປ່ຽນແປງລົ້ມເຫລວ");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (id: number) => {
    if (activeTab === "sections") {
      setError("ບໍ່ສາມາດລຶບສ່ວນຕ່າງໆໄດ້");
      return;
    }

    // Determine label text for the item being deleted
    const getLabelForCurrentTab = (): string => {
      try {
        if (!content) return "";
        const tab = activeTab;
        const list: any[] = (content as any)[tab] || [];
        const item = list.find((it: any) => it.id === id);
        if (!item) return "";
        switch (tab) {
          case "features":
            return item.title || "";
          case "testimonials":
            return item.name || "";
          case "faqs":
            return item.question || "";
          case "logos":
            return item.logo_type || "";
          case "stats":
            return item.stat_title || "";
          case "socialMedia":
            return item.platform_name || "";
          default:
            return "";
        }
      } catch {
        return "";
      }
    };

    const endpoint =
      activeTab === "socialMedia"
        ? `/landing-page/admin/social-media/${id}`
        : `/landing-page/admin/${activeTab}/${id}`;

    setDeleteTarget({ id, label: getLabelForCurrentTab(), endpoint });
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await api.delete(deleteTarget.endpoint);
      setShowDeleteConfirm(false);
      setDeleteTarget(null);
      await fetchContent();
    } catch (err: any) {
      console.error("Error deleting:", err);
      setError("ການລຶບລາຍການລົ້ມເຫລວ");
    } finally {
      setDeleting(false);
    }
  };

  const openEditModal = (item: any = null) => {
    setEditingItem(item || { id: "new" });
    setFormData({
      title: item?.title || "",
      subtitle: item?.subtitle || "",
      content: item?.content || "",
      button_text: item?.button_text || "",
      button_link: item?.button_link || "",
      name: item?.name || "",
      role: item?.role || "",
      description: item?.description || "",
      icon_name: item?.icon_name || "",
      link_text: item?.link_text || "",
      link_url: item?.link_url || "",
      question: item?.question || "",
      answer: item?.answer || "",
      value: item?.setting_value || "",
      rating: item?.rating || 5,
      avatar_url: item?.avatar_url || "",
      logo_type: item?.logo_type || "",
      logo_url: item?.logo_url || "",
      logo_alt_text: item?.logo_alt_text || "",
      stat_title: item?.stat_title || "",
      stat_value: item?.stat_value || "",
      stat_description: item?.stat_description || "",
      stat_icon: item?.stat_icon || "",
      platform_name: item?.platform_name || "",
      platform_icon: item?.platform_icon || "",
      profile_url: item?.profile_url || "",
      display_name: item?.display_name || "",
      sort_order: item?.sort_order || 0,
      is_active: item?.is_active !== undefined ? item.is_active : true,
    });
    setShowEditModal(true);
  };

  const getIconComponent = (iconName: string) => {
    const iconMap: { [key: string]: any } = {
      AcademicCapIcon,
      UserGroupIcon,
      VideoCameraIcon,
      DocumentTextIcon,
      StarIcon,
      EnvelopeIcon,
      PhoneIcon,
      MapPinIcon,
      // Add more icons as needed - these will fall back to AcademicCapIcon if not imported
    };
    return iconMap[iconName] || AcademicCapIcon;
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev: any) => ({ ...prev, [field]: value }));
  };

  // Available icons for selection
  const availableIcons = [
    {
      name: "AcademicCapIcon",
      component: AcademicCapIcon,
      label: "🎓 Academic Cap",
    },
    { name: "UserGroupIcon", component: UserGroupIcon, label: "👥 User Group" },
    {
      name: "VideoCameraIcon",
      component: VideoCameraIcon,
      label: "📹 Video Camera",
    },
    {
      name: "DocumentTextIcon",
      component: DocumentTextIcon,
      label: "📄 Document",
    },
    { name: "StarIcon", component: StarIcon, label: "⭐ Star" },
    { name: "EnvelopeIcon", component: EnvelopeIcon, label: "✉️ Envelope" },
    { name: "PhoneIcon", component: PhoneIcon, label: "📞 Phone" },
    { name: "MapPinIcon", component: MapPinIcon, label: "📍 Map Pin" },
    { name: "BookOpenIcon", component: BookOpenIcon, label: "📖 Book Open" },
    { name: "LightBulbIcon", component: LightBulbIcon, label: "💡 Light Bulb" },
    { name: "ChartBarIcon", component: ChartBarIcon, label: "📊 Chart Bar" },
    { name: "ClockIcon", component: ClockIcon, label: "🕐 Clock" },
    {
      name: "CheckCircleIcon",
      component: CheckCircleIcon,
      label: "✅ Check Circle",
    },
    { name: "HeartIcon", component: HeartIcon, label: "❤️ Heart" },
    { name: "TrophyIcon", component: TrophyIcon, label: "🏆 Trophy" },
    { name: "FireIcon", component: FireIcon, label: "🔥 Fire" },
    { name: "SparklesIcon", component: SparklesIcon, label: "✨ Sparkles" },
    { name: "GlobeAltIcon", component: GlobeAltIcon, label: "🌍 Globe" },
    { name: "CogIcon", component: CogIcon, label: "⚙️ Cog" },
    {
      name: "ShieldCheckIcon",
      component: ShieldCheckIcon,
      label: "🛡️ Shield Check",
    },
    { name: "HomeIcon", component: HomeIcon, label: "🏠 Home" },
    { name: "UserIcon", component: UserIcon, label: "👤 User" },
    { name: "Cog6ToothIcon", component: Cog6ToothIcon, label: "⚙️ Settings" },
    { name: "BellIcon", component: BellIcon, label: "🔔 Bell" },
    {
      name: "MagnifyingGlassIcon",
      component: MagnifyingGlassIcon,
      label: "🔍 Search",
    },
    {
      name: "ChatBubbleLeftIcon",
      component: ChatBubbleLeftIcon,
      label: "💬 Chat",
    },
    { name: "CameraIcon", component: CameraIcon, label: "📷 Camera" },
    { name: "MusicalNoteIcon", component: MusicalNoteIcon, label: "🎵 Music" },
    { name: "GiftIcon", component: GiftIcon, label: "🎁 Gift" },
    {
      name: "ShoppingBagIcon",
      component: ShoppingBagIcon,
      label: "🛍️ Shopping",
    },
    {
      name: "CreditCardIcon",
      component: CreditCardIcon,
      label: "💳 Credit Card",
    },
    { name: "TruckIcon", component: TruckIcon, label: "🚚 Truck" },
    {
      name: "WrenchScrewdriverIcon",
      component: WrenchScrewdriverIcon,
      label: "🔧 Tools",
    },
    {
      name: "PaintBrushIcon",
      component: PaintBrushIcon,
      label: "🎨 Paint Brush",
    },
    {
      name: "ComputerDesktopIcon",
      component: ComputerDesktopIcon,
      label: "💻 Computer",
    },
    {
      name: "DevicePhoneMobileIcon",
      component: DevicePhoneMobileIcon,
      label: "📱 Mobile",
    },
    {
      name: "SpeakerWaveIcon",
      component: SpeakerWaveIcon,
      label: "🔊 Speaker",
    },
    {
      name: "MicrophoneIcon",
      component: MicrophoneIcon,
      label: "🎤 Microphone",
    },
    {
      name: "VideoCameraSlashIcon",
      component: VideoCameraSlashIcon,
      label: "📹 Video Off",
    },
    { name: "PhotoIcon", component: PhotoIcon, label: "📸 Photo" },
    { name: "FilmIcon", component: FilmIcon, label: "🎬 Film" },
    { name: "PlayIcon", component: PlayIcon, label: "▶️ Play" },
    { name: "PauseIcon", component: PauseIcon, label: "⏸️ Pause" },
    { name: "StopIcon", component: StopIcon, label: "⏹️ Stop" },
    { name: "ForwardIcon", component: ForwardIcon, label: "⏭️ Forward" },
    { name: "BackwardIcon", component: BackwardIcon, label: "⏮️ Backward" },
    { name: "WifiIcon", component: WifiIcon, label: "📶 WiFi" },
    { name: "SignalIcon", component: SignalIcon, label: "📡 Signal" },
    { name: "SunIcon", component: SunIcon, label: "☀️ Sun" },
    { name: "MoonIcon", component: MoonIcon, label: "🌙 Moon" },
    { name: "CloudIcon", component: CloudIcon, label: "☁️ Cloud" },
    { name: "BoltIcon", component: BoltIcon, label: "⚡ Bolt" },
    // Social Media Icons
    { name: "ShareIcon", component: ShareIcon, label: "📤 Share" },
    { name: "LinkIcon", component: LinkIcon, label: "🔗 Link" },
    { name: "AtSymbolIcon", component: AtSymbolIcon, label: "@ At Symbol" },
    { name: "HashtagIcon", component: HashtagIcon, label: "# Hashtag" },
    { name: "GlobeAltIcon", component: GlobeAltIcon, label: "🌐 Globe" },
    {
      name: "ChatBubbleLeftIcon",
      component: ChatBubbleLeftIcon,
      label: "💬 Chat",
    },
    { name: "EnvelopeIcon", component: EnvelopeIcon, label: "✉️ Email" },
    { name: "PhoneIcon", component: PhoneIcon, label: "📞 Phone" },
    { name: "CameraIcon", component: CameraIcon, label: "📷 Camera" },
    { name: "VideoCameraIcon", component: VideoCameraIcon, label: "📹 Video" },
    { name: "HeartIcon", component: HeartIcon, label: "❤️ Heart" },
    { name: "StarIcon", component: StarIcon, label: "⭐ Star" },
    { name: "UserIcon", component: UserIcon, label: "👤 User" },
    { name: "UserGroupIcon", component: UserGroupIcon, label: "👥 Group" },
    {
      name: "PaperAirplaneIcon",
      component: PaperAirplaneIcon,
      label: "✈️ Paper Airplane",
    },
  ];

  const openIconSelector = (field: string) => {
    setIconSelectorField(field);
    setShowIconSelector(true);
  };

  const selectIcon = (iconName: string) => {
    setFormData((prev: any) => ({ ...prev, [iconSelectorField]: iconName }));
    setShowIconSelector(false);
  };

  // Social Media SVG Icons
  const socialMediaIcons = [
    {
      name: "facebook",
      label: "Facebook",
      svg: `<svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
      </svg>`,
    },
    {
      name: "whatsapp",
      label: "WhatsApp",
      svg: `<svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
      </svg>`,
    },
    {
      name: "line",
      label: "Line",
      svg: `<svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
        <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314"/>
      </svg>`,
    },
    {
      name: "instagram",
      label: "Instagram",
      svg: `<svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
      </svg>`,
    },
    {
      name: "youtube",
      label: "YouTube",
      svg: `<svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
      </svg>`,
    },
    {
      name: "twitter",
      label: "Twitter",
      svg: `<svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
        <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
      </svg>`,
    },
    {
      name: "telegram",
      label: "Telegram",
      svg: `<svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
        <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
      </svg>`,
    },
    {
      name: "tiktok",
      label: "TikTok",
      svg: `<svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
        <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
      </svg>`,
    },
  ];

  const handleImageUpload = async (
    file: File,
    fieldType: "logo" | "avatar" = "logo",
  ) => {
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      setError("ກະລຸນາເລືອກໄຟລ໌ຮູບພາບ");
      return;
    }

    // Validate file size (5MB limit)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      setError("ຂະໜາດໄຟລ໌ຕ້ອງບໍ່ເກີນ 5MB");
      return;
    }

    try {
      setUploadingImage(true);
      setError(null); // Clear any previous errors

      // Upload image to server
      const formData = new FormData();
      formData.append("image", file);
      formData.append("type", fieldType);

      const response = await apiUpload.post(
        "/landing-page/upload/image",
        formData,
      );

      if (response.data.url) {
        const fieldName = fieldType === "logo" ? "logo_url" : "avatar_url";
        setFormData((prev: any) => ({
          ...prev,
          [fieldName]: response.data.url,
        }));
        console.log(
          `${fieldType} image uploaded successfully:`,
          response.data.url,
        );
      } else {
        throw new Error("No URL returned from server");
      }
    } catch (err: any) {
      console.error("Error uploading image:", err);
      setError("ການອັບໂຫລດຮູບພາບລົ້ມເຫລວ");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleImageUpload(file, currentImageField);
    }
  };

  const openFileSelector = (fieldType: "logo" | "avatar" = "logo") => {
    setCurrentImageField(fieldType);
    fileInputRef.current?.click();
  };

  // Cleanup blob URLs when component unmounts or form data changes
  useEffect(() => {
    return () => {
      // Cleanup any blob URLs to prevent memory leaks
      if (formData.logo_url && formData.logo_url.startsWith("blob:")) {
        URL.revokeObjectURL(formData.logo_url);
      }
      if (formData.avatar_url && formData.avatar_url.startsWith("blob:")) {
        URL.revokeObjectURL(formData.avatar_url);
      }
    };
  }, [formData.logo_url, formData.avatar_url]);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent, fieldType: "logo" | "avatar") => {
    e.preventDefault();
    e.stopPropagation();

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      setCurrentImageField(fieldType);
      handleImageUpload(files[0], fieldType);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={fetchContent}
          className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-4 md:mb-6 lg:mb-8">
        <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">
          ຈັດການໜ້າຫຼັກ
        </h1>
        <p className="mt-2 text-xs sm:text-sm md:text-base text-gray-600">
          ແກ້ໄຂເນື້ອຫາຂອງໜ້າຫຼັກຂອງເວັບໄຊທ໌
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-4 md:mb-5 lg:mb-6">
        <nav className="-mb-px flex flex-wrap gap-1 sm:gap-2 md:gap-4 lg:gap-8 overflow-x-auto">
          {[
            { key: "sections", label: "ສ່ວນຕ່າງໆ" },
            { key: "features", label: "ຄຸນສົມບັດ" },
            { key: "testimonials", label: "ຄຳເຫັນ" },
            { key: "faqs", label: "ຄຳຖາມທີ່ພົບເປັນປະຈຳ" },
            { key: "logos", label: "ໂລໂກ້" },
            { key: "stats", label: "ສະຖິຕິ" },
            { key: "socialMedia", label: "ສື່ສັງຄົມ" },
            { key: "settings", label: "ການຕັ້ງຄ່າ" },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as any)}
              className={`py-2 px-2 md:px-3 lg:px-1 border-b-2 font-medium text-xs sm:text-sm md:text-base whitespace-nowrap ${
                activeTab === tab.key
                  ? "border-indigo-500 text-indigo-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-2 sm:px-4 md:px-6 py-2 sm:py-3 md:py-4 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
            <h2 className="text-base sm:text-lg md:text-xl font-medium text-gray-900">
              {activeTab === "sections" && "ສ່ວນຕ່າງໆຂອງໜ້າຫຼັກ"}
              {activeTab === "features" && "ຄຸນສົມບັດຂອງເວັບໄຊທ໌"}
              {activeTab === "testimonials" && "ຄຳເຫັນຂອງນັກຮຽນ"}
              {activeTab === "faqs" && "ຄຳຖາມທີ່ພົບເປັນປະຈຳ"}
              {activeTab === "logos" && "ໂລໂກ້ຂອງເວັບໄຊທ໌"}
              {activeTab === "stats" && "ສະຖິຕິຂອງເວັບໄຊທ໌"}
              {activeTab === "socialMedia" && "ສື່ສັງຄົມຂອງເວັບໄຊທ໌"}
              {activeTab === "settings" && "ການຕັ້ງຄ່າທົ່ວໄປ"}
            </h2>
            {activeTab !== "sections" && activeTab !== "settings" && (
              <button
                onClick={() => openEditModal()}
                className="flex items-center px-2 sm:px-3 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-xs sm:text-sm"
              >
                <PlusIcon className="h-4 w-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">ເພີ່ມໃໝ່</span>
              </button>
            )}
          </div>
        </div>

        <div className="p-2 sm:p-4 md:p-6 overflow-hidden">
          {/* Sections */}
          {activeTab === "sections" && content?.sections && (
            <div className="space-y-2 sm:space-y-3 md:space-y-4">
              {content.sections.map((section) => (
                <div
                  key={section.id}
                  className="border rounded-lg p-2 sm:p-3 md:p-4"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div>
                      <h3 className="font-medium text-gray-900">
                        {section.section_name}
                      </h3>
                      <p className="text-xs sm:text-sm text-gray-500">
                        {section.title}
                      </p>
                    </div>
                    <div className="flex items-center space-x-1 sm:space-x-2">
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${
                          section.is_active
                            ? "bg-green-100 text-green-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {section.is_active ? "ເປີດໃຊ້" : "ປິດໃຊ້"}
                      </span>
                      <button
                        onClick={() => openEditModal(section)}
                        className="text-indigo-600 hover:text-indigo-900"
                        title="ແກ້ໄຂສ່ວນນີ້"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Features */}
          {activeTab === "features" && content?.features && (
            <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
              {content.features.map((feature) => {
                const IconComponent = getIconComponent(feature.icon_name);
                return (
                  <div
                    key={feature.id}
                    className="border rounded-lg p-2 sm:p-3 md:p-4"
                  >
                    <div className="flex items-center justify-between mb-1 sm:mb-2 md:mb-3">
                      <div className="flex items-center min-w-0 flex-1">
                        <IconComponent className="h-4 w-4 md:h-5 md:w-5 text-indigo-600 mr-2 flex-shrink-0" />
                        <h3 className="font-medium text-gray-900 text-xs sm:text-sm md:text-base truncate">
                          {feature.title}
                        </h3>
                      </div>
                      <div className="flex items-center space-x-1 md:space-x-2 flex-shrink-0">
                        <span
                          className={`px-1 md:px-2 py-0.5 md:py-1 text-xs rounded-full ${
                            feature.is_active
                              ? "bg-green-100 text-green-800"
                              : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {feature.is_active ? "ເປີດ" : "ປິດ"}
                        </span>
                        <button
                          onClick={() => openEditModal(feature)}
                          className="text-indigo-600 hover:text-indigo-900 p-1"
                          title="ແກ້ໄຂຄຸນສົມບັດ"
                        >
                          <PencilIcon className="h-3 w-3 md:h-4 md:w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(feature.id)}
                          className="text-red-600 hover:text-red-900 p-1"
                          title="ລຶບຄຸນສົມບັດ"
                        >
                          <TrashIcon className="h-3 w-3 md:h-4 md:w-4" />
                        </button>
                      </div>
                    </div>
                    <p className="text-xs md:text-sm text-gray-600 line-clamp-2">
                      {feature.description}
                    </p>
                  </div>
                );
              })}
            </div>
          )}

          {/* Testimonials */}
          {activeTab === "testimonials" && content?.testimonials && (
            <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
              {content.testimonials.map((testimonial) => (
                <div
                  key={testimonial.id}
                  className="border rounded-lg p-2 sm:p-3 md:p-4"
                >
                  <div className="flex items-center justify-between mb-1 sm:mb-2 md:mb-3">
                    <div className="min-w-0 flex-1">
                      <h3 className="font-medium text-gray-900 text-xs sm:text-sm md:text-base truncate">
                        {testimonial.name}
                      </h3>
                      <p className="text-xs md:text-sm text-gray-500 truncate">
                        {testimonial.role}
                      </p>
                    </div>
                    <div className="flex items-center space-x-1 md:space-x-2 flex-shrink-0">
                      <span
                        className={`px-1 md:px-2 py-0.5 md:py-1 text-xs rounded-full ${
                          testimonial.is_active
                            ? "bg-green-100 text-green-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {testimonial.is_active ? "ເປີດ" : "ປິດ"}
                      </span>
                      <button
                        onClick={() => openEditModal(testimonial)}
                        className="text-indigo-600 hover:text-indigo-900 p-1"
                        title="ແກ້ໄຂຄຳເຫັນ"
                      >
                        <PencilIcon className="h-3 w-3 md:h-4 md:w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(testimonial.id)}
                        className="text-red-600 hover:text-red-900 p-1"
                        title="ລຶບຄຳເຫັນ"
                      >
                        <TrashIcon className="h-3 w-3 md:h-4 md:w-4" />
                      </button>
                    </div>
                  </div>
                  <p className="text-xs md:text-sm text-gray-600 italic line-clamp-3">
                    "{testimonial.content}"
                  </p>
                </div>
              ))}
            </div>
          )}

          {/* FAQs */}
          {activeTab === "faqs" && content?.faqs && (
            <div className="space-y-2 sm:space-y-3 md:space-y-4">
              {content.faqs.map((faq) => (
                <div
                  key={faq.id}
                  className="border rounded-lg p-2 sm:p-3 md:p-4"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                    <h3 className="font-medium text-gray-900">
                      {faq.question}
                    </h3>
                    <div className="flex items-center space-x-1 sm:space-x-2">
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${
                          faq.is_active
                            ? "bg-green-100 text-green-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {faq.is_active ? "ເປີດໃຊ້" : "ປິດໃຊ້"}
                      </span>
                      <button
                        onClick={() => openEditModal(faq)}
                        className="text-indigo-600 hover:text-indigo-900"
                        title="ແກ້ໄຂຄຳຖາມ"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(faq.id)}
                        className="text-red-600 hover:text-red-900"
                        title="ລຶບຄຳຖາມ"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <p className="text-xs sm:text-sm text-gray-600">
                    {faq.answer}
                  </p>
                </div>
              ))}
            </div>
          )}

          {/* Logos */}
          {activeTab === "logos" && content?.logos && (
            <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
              {content.logos.map((logo) => (
                <div
                  key={logo.id}
                  className="border rounded-lg p-2 sm:p-3 md:p-4"
                >
                  <div className="flex items-center justify-between mb-1 sm:mb-2 md:mb-3">
                    <div className="min-w-0 flex-1">
                      <h3 className="font-medium text-gray-900 text-xs sm:text-sm md:text-base truncate">
                        {logo.logo_type}
                      </h3>
                      <p className="text-xs md:text-sm text-gray-500 truncate">
                        {logo.logo_alt_text}
                      </p>
                    </div>
                    <div className="flex items-center space-x-1 md:space-x-2 flex-shrink-0">
                      <span
                        className={`px-1 md:px-2 py-0.5 md:py-1 text-xs rounded-full ${
                          logo.is_active
                            ? "bg-green-100 text-green-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {logo.is_active ? "ເປີດ" : "ປິດ"}
                      </span>
                      <button
                        onClick={() => openEditModal(logo)}
                        className="text-indigo-600 hover:text-indigo-900 p-1"
                        title="ແກ້ໄຂໂລໂກ້"
                      >
                        <PencilIcon className="h-3 w-3 md:h-4 md:w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(logo.id)}
                        className="text-red-600 hover:text-red-900 p-1"
                        title="ລຶບໂລໂກ້"
                      >
                        <TrashIcon className="h-3 w-3 md:h-4 md:w-4" />
                      </button>
                    </div>
                  </div>
                  {logo.logo_url && (
                    <div className="mt-1 sm:mt-2 md:mt-3">
                      <img
                        src={logo.logo_url}
                        alt={logo.logo_alt_text}
                        className="h-8 md:h-12 w-auto object-contain"
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Stats */}
          {activeTab === "stats" && content?.stats && (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3 md:gap-4">
              {content.stats.map((stat) => (
                <div
                  key={stat.id}
                  className="border rounded-lg p-2 sm:p-3 md:p-4"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                    <div>
                      <h3 className="font-medium text-gray-900">
                        {stat.stat_title}
                      </h3>
                      <p className="text-xs sm:text-sm text-gray-500">
                        {stat.stat_description}
                      </p>
                    </div>
                    <div className="flex items-center space-x-1 sm:space-x-2">
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${
                          stat.is_active
                            ? "bg-green-100 text-green-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {stat.is_active ? "ເປີດໃຊ້" : "ປິດໃຊ້"}
                      </span>
                      <button
                        onClick={() => openEditModal(stat)}
                        className="text-indigo-600 hover:text-indigo-900"
                        title="ແກ້ໄຂສະຖິຕິ"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(stat.id)}
                        className="text-red-600 hover:text-red-900"
                        title="ລຶບສະຖິຕິ"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <div className="text-xl sm:text-2xl font-bold text-indigo-600">
                    {stat.stat_value}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Social Media */}
          {activeTab === "socialMedia" && content?.socialMedia && (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3 md:gap-4">
              {content.socialMedia.map((social) => (
                <div
                  key={social.id}
                  className="border rounded-lg p-2 sm:p-3 md:p-4"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                    <div className="flex items-center space-x-2 sm:space-x-3">
                      {social.platform_icon && (
                        <div className="flex items-center justify-center w-8 h-8 bg-gray-100 rounded-lg">
                          {social.platform_icon.startsWith("<svg") ? (
                            <div
                              className="h-5 w-5 text-gray-600"
                              dangerouslySetInnerHTML={{
                                __html: social.platform_icon,
                              }}
                            />
                          ) : (
                            <div className="h-5 w-5 text-gray-600 flex items-center justify-center">
                              {getIconComponent(social.platform_icon) &&
                                React.createElement(
                                  getIconComponent(social.platform_icon),
                                  { className: "h-5 w-5" },
                                )}
                            </div>
                          )}
                        </div>
                      )}
                      <div>
                        <h3 className="font-medium text-gray-900">
                          {social.platform_name}
                        </h3>
                        <p className="text-xs sm:text-sm text-gray-500">
                          {social.display_name}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1 sm:space-x-2">
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${
                          social.is_active
                            ? "bg-green-100 text-green-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {social.is_active ? "ເປີດໃຊ້" : "ປິດໃຊ້"}
                      </span>
                      <button
                        onClick={() => openEditModal(social)}
                        className="text-indigo-600 hover:text-indigo-900"
                        title="ແກ້ໄຂສື່ສັງຄົມ"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(social.id)}
                        className="text-red-600 hover:text-red-900"
                        title="ລຶບສື່ສັງຄົມ"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <a
                    href={social.profile_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-600 hover:text-indigo-800 text-xs sm:text-sm break-all"
                  >
                    {social.profile_url}
                  </a>
                </div>
              ))}
            </div>
          )}

          {/* Settings */}
          {activeTab === "settings" && content?.settings && (
            <div className="space-y-2 sm:space-y-3 md:space-y-4">
              {content.settings.map((setting) => (
                <div
                  key={setting.id}
                  className="border rounded-lg p-2 sm:p-3 md:p-4"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div>
                      <h3 className="font-medium text-gray-900">
                        {setting.setting_key}
                      </h3>
                      <p className="text-xs sm:text-sm text-gray-500">
                        {setting.description}
                      </p>
                      <p className="text-xs sm:text-sm text-gray-600 mt-1">
                        Value: {setting.setting_value}
                        {setting.setting_key === "site_title" && (
                          <span className="ml-2 text-xs text-blue-600">
                            (Browser tab title)
                          </span>
                        )}
                        {setting.setting_key === "site_favicon" && (
                          <span className="ml-2 text-xs text-blue-600">
                            (Website icon)
                          </span>
                        )}
                      </p>
                    </div>
                    <button
                      onClick={() => openEditModal(setting)}
                      className="text-indigo-600 hover:text-indigo-900"
                      title="ແກ້ໄຂການຕັ້ງຄ່າ"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && editingItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-2 sm:p-4 md:p-6 max-w-xs sm:max-w-md w-full mx-2 sm:mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2 sm:mb-4">
              {editingItem.id === "new" ? "ເພີ່ມໃໝ່" : "ແກ້ໄຂ"}
            </h3>

            <div className="space-y-2 sm:space-y-4">
              {/* The forms below are already responsive due to w-full and px/py classes */}
              {activeTab === "settings" ? (
                // Settings form
                <div>
                  <div className="mb-2 sm:mb-4">
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Setting Key
                    </label>
                    <input
                      type="text"
                      value={editingItem.setting_key || ""}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                      aria-label="Setting key"
                    />
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Value
                    </label>
                    <input
                      type="text"
                      value={formData.value}
                      onChange={(e) =>
                        handleInputChange("value", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="ປ້ອນຄ່າການຕັ້ງຄ່າ"
                    />
                  </div>
                </div>
              ) : activeTab === "sections" ? (
                // Sections form
                <div className="space-y-2 sm:space-y-4">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Title
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) =>
                        handleInputChange("title", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Subtitle
                    </label>
                    <input
                      type="text"
                      value={formData.subtitle}
                      onChange={(e) =>
                        handleInputChange("subtitle", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Content
                    </label>
                    <textarea
                      value={formData.content}
                      onChange={(e) =>
                        handleInputChange("content", e.target.value)
                      }
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Button Text
                    </label>
                    <input
                      type="text"
                      value={formData.button_text}
                      onChange={(e) =>
                        handleInputChange("button_text", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Button Link
                    </label>
                    <input
                      type="text"
                      value={formData.button_link}
                      onChange={(e) =>
                        handleInputChange("button_link", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Active
                    </label>
                    <select
                      value={formData.is_active ? "true" : "false"}
                      onChange={(e) =>
                        handleInputChange(
                          "is_active",
                          e.target.value === "true",
                        )
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="true">ເປີດໃຊ້</option>
                      <option value="false">ປິດໃຊ້</option>
                    </select>
                  </div>
                </div>
              ) : activeTab === "features" ? (
                // Features form
                <div className="space-y-2 sm:space-y-4">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Title
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) =>
                        handleInputChange("title", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) =>
                        handleInputChange("description", e.target.value)
                      }
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Icon
                    </label>
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        value={formData.icon_name}
                        onChange={(e) =>
                          handleInputChange("icon_name", e.target.value)
                        }
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="ເລືອກໄອຄອນ"
                      />
                      <button
                        type="button"
                        onClick={() => openIconSelector("icon_name")}
                        className="px-3 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                        title="ເລືອກໄອຄອນ"
                      >
                        ເລືອກ
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Link Text
                    </label>
                    <input
                      type="text"
                      value={formData.link_text}
                      onChange={(e) =>
                        handleInputChange("link_text", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Link URL
                    </label>
                    <input
                      type="text"
                      value={formData.link_url}
                      onChange={(e) =>
                        handleInputChange("link_url", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Sort Order
                    </label>
                    <input
                      type="number"
                      value={formData.sort_order}
                      onChange={(e) =>
                        handleInputChange(
                          "sort_order",
                          parseInt(e.target.value) || 0,
                        )
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Active
                    </label>
                    <select
                      value={formData.is_active ? "true" : "false"}
                      onChange={(e) =>
                        handleInputChange(
                          "is_active",
                          e.target.value === "true",
                        )
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="true">ເປີດໃຊ້</option>
                      <option value="false">ປິດໃຊ້</option>
                    </select>
                  </div>
                </div>
              ) : activeTab === "testimonials" ? (
                // Testimonials form
                <div className="space-y-2 sm:space-y-4">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Name
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) =>
                        handleInputChange("name", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Role
                    </label>
                    <input
                      type="text"
                      value={formData.role}
                      onChange={(e) =>
                        handleInputChange("role", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Content
                    </label>
                    <textarea
                      value={formData.content}
                      onChange={(e) =>
                        handleInputChange("content", e.target.value)
                      }
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Avatar URL
                    </label>
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        value={formData.avatar_url}
                        onChange={(e) =>
                          handleInputChange("avatar_url", e.target.value)
                        }
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="Enter avatar URL or upload image"
                      />
                      <button
                        type="button"
                        onClick={() => openFileSelector("avatar")}
                        disabled={uploadingImage}
                        className="px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center"
                        title="Upload image from device"
                      >
                        {uploadingImage ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        ) : (
                          <>
                            <CameraIcon className="h-4 w-4 mr-1" />
                            <span className="hidden sm:inline">Upload</span>
                          </>
                        )}
                      </button>
                    </div>
                    {formData.avatar_url && (
                      <div className="mt-2 p-2 border border-gray-200 rounded-md bg-gray-50">
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Preview:
                        </label>
                        <img
                          src={formData.avatar_url}
                          alt="Avatar preview"
                          className="h-16 w-16 rounded-full object-cover"
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      </div>
                    )}
                    <div
                      className="mt-2 p-3 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 hover:border-indigo-400 transition-colors cursor-pointer"
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, "avatar")}
                      onClick={() => openFileSelector("avatar")}
                    >
                      <div className="text-center">
                        <CameraIcon className="mx-auto h-8 w-8 text-gray-400" />
                        <p className="mt-1 text-xs text-gray-600">
                          ຫຼື ລາກແລະວາງຮູບພາບທີ່ນີ້
                        </p>
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Rating
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="5"
                      value={formData.rating}
                      onChange={(e) =>
                        handleInputChange(
                          "rating",
                          parseInt(e.target.value) || 5,
                        )
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Sort Order
                    </label>
                    <input
                      type="number"
                      value={formData.sort_order}
                      onChange={(e) =>
                        handleInputChange(
                          "sort_order",
                          parseInt(e.target.value) || 0,
                        )
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Active
                    </label>
                    <select
                      value={formData.is_active ? "true" : "false"}
                      onChange={(e) =>
                        handleInputChange(
                          "is_active",
                          e.target.value === "true",
                        )
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="true">ເປີດໃຊ້</option>
                      <option value="false">ປິດໃຊ້</option>
                    </select>
                  </div>
                </div>
              ) : activeTab === "faqs" ? (
                // FAQs form
                <div className="space-y-2 sm:space-y-4">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Question
                    </label>
                    <input
                      type="text"
                      value={formData.question}
                      onChange={(e) =>
                        handleInputChange("question", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Answer
                    </label>
                    <textarea
                      value={formData.answer}
                      onChange={(e) =>
                        handleInputChange("answer", e.target.value)
                      }
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Sort Order
                    </label>
                    <input
                      type="number"
                      value={formData.sort_order}
                      onChange={(e) =>
                        handleInputChange(
                          "sort_order",
                          parseInt(e.target.value) || 0,
                        )
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Active
                    </label>
                    <select
                      value={formData.is_active ? "true" : "false"}
                      onChange={(e) =>
                        handleInputChange(
                          "is_active",
                          e.target.value === "true",
                        )
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="true">ເປີດໃຊ້</option>
                      <option value="false">ປິດໃຊ້</option>
                    </select>
                  </div>
                </div>
              ) : activeTab === "logos" ? (
                // Logos form
                <div className="space-y-2 sm:space-y-4">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Logo Type
                    </label>
                    <select
                      value={formData.logo_type}
                      onChange={(e) =>
                        handleInputChange("logo_type", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="">ເລືອກປະເພດໂລໂກ້</option>
                      <option value="navbar">Navbar Logo</option>
                      <option value="footer">Footer Logo</option>
                      <option value="favicon">Favicon</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Logo URL
                    </label>
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        value={formData.logo_url}
                        onChange={(e) =>
                          handleInputChange("logo_url", e.target.value)
                        }
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="Enter logo URL or upload image"
                      />
                      <button
                        type="button"
                        onClick={() => openFileSelector("logo")}
                        disabled={uploadingImage}
                        className="px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center"
                        title="Upload image from device"
                      >
                        {uploadingImage ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        ) : (
                          <>
                            <CameraIcon className="h-4 w-4 mr-1" />
                            <span className="hidden sm:inline">Upload</span>
                          </>
                        )}
                      </button>
                    </div>
                    {formData.logo_url && (
                      <div className="mt-2 p-2 border border-gray-200 rounded-md bg-gray-50">
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Preview:
                        </label>
                        <img
                          src={formData.logo_url}
                          alt="Logo preview"
                          className="h-16 w-auto object-contain max-w-full"
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      </div>
                    )}
                    <div
                      className="mt-2 p-3 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 hover:border-indigo-400 transition-colors cursor-pointer"
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, "logo")}
                      onClick={() => openFileSelector("logo")}
                    >
                      <div className="text-center">
                        <CameraIcon className="mx-auto h-8 w-8 text-gray-400" />
                        <p className="mt-1 text-xs text-gray-600">
                          ຫຼື ລາກແລະວາງຮູບພາບທີ່ນີ້
                        </p>
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Alt Text
                    </label>
                    <input
                      type="text"
                      value={formData.logo_alt_text}
                      onChange={(e) =>
                        handleInputChange("logo_alt_text", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Enter alt text"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Sort Order
                    </label>
                    <input
                      type="number"
                      value={formData.sort_order}
                      onChange={(e) =>
                        handleInputChange(
                          "sort_order",
                          parseInt(e.target.value) || 0,
                        )
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Active
                    </label>
                    <select
                      value={formData.is_active ? "true" : "false"}
                      onChange={(e) =>
                        handleInputChange(
                          "is_active",
                          e.target.value === "true",
                        )
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="true">ເປີດໃຊ້</option>
                      <option value="false">ປິດໃຊ້</option>
                    </select>
                  </div>
                </div>
              ) : activeTab === "stats" ? (
                // Stats form
                <div className="space-y-2 sm:space-y-4">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Stat Title
                    </label>
                    <input
                      type="text"
                      value={formData.stat_title}
                      onChange={(e) =>
                        handleInputChange("stat_title", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Enter stat title"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Stat Value
                    </label>
                    <input
                      type="text"
                      value={formData.stat_value}
                      onChange={(e) =>
                        handleInputChange("stat_value", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Enter stat value"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      value={formData.stat_description}
                      onChange={(e) =>
                        handleInputChange("stat_description", e.target.value)
                      }
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Enter stat description"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Icon
                    </label>
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        value={formData.stat_icon}
                        onChange={(e) =>
                          handleInputChange("stat_icon", e.target.value)
                        }
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="Enter icon name"
                      />
                      <button
                        type="button"
                        onClick={() => openIconSelector("stat_icon")}
                        className="px-3 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                        title="ເລືອກໄອຄອນ"
                      >
                        ເລືອກ
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Sort Order
                    </label>
                    <input
                      type="number"
                      value={formData.sort_order}
                      onChange={(e) =>
                        handleInputChange(
                          "sort_order",
                          parseInt(e.target.value) || 0,
                        )
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Active
                    </label>
                    <select
                      value={formData.is_active ? "true" : "false"}
                      onChange={(e) =>
                        handleInputChange(
                          "is_active",
                          e.target.value === "true",
                        )
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="true">ເປີດໃຊ້</option>
                      <option value="false">ປິດໃຊ້</option>
                    </select>
                  </div>
                </div>
              ) : activeTab === "socialMedia" ? (
                // Social Media form
                <div className="space-y-2 sm:space-y-4">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Platform Type
                    </label>
                    <select
                      value=""
                      onChange={(e) => {
                        const platform = e.target.value;
                        if (platform) {
                          const socialIcon = socialMediaIcons.find(
                            (icon) => icon.name === platform,
                          );
                          if (socialIcon) {
                            handleInputChange(
                              "platform_name",
                              socialIcon.label,
                            );
                            handleInputChange("platform_icon", socialIcon.svg);
                          } else if (platform === "custom") {
                            handleInputChange("platform_name", "");
                            handleInputChange("platform_icon", "");
                          }
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="">ເລືອກປະເພດສື່ສັງຄົມ</option>
                      <option value="facebook">Facebook</option>
                      <option value="whatsapp">WhatsApp</option>
                      <option value="line">Line</option>
                      <option value="instagram">Instagram</option>
                      <option value="youtube">YouTube</option>
                      <option value="twitter">Twitter</option>
                      <option value="linkedin">LinkedIn</option>
                      <option value="telegram">Telegram</option>
                      <option value="tiktok">TikTok</option>
                      <option value="custom">ປັດໄຈຕົວ</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Platform Name
                    </label>
                    <input
                      type="text"
                      value={formData.platform_name}
                      onChange={(e) =>
                        handleInputChange("platform_name", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="ປ້ອນຊື່ສື່ສັງຄົມ"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Platform Icon
                    </label>
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        value={formData.platform_icon}
                        onChange={(e) =>
                          handleInputChange("platform_icon", e.target.value)
                        }
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="ເລືອກໄອຄອນສື່ສັງຄົມ"
                      />
                      <button
                        type="button"
                        onClick={() => openIconSelector("platform_icon")}
                        className="px-3 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                        title="ເລືອກໄອຄອນສື່ສັງຄົມ"
                      >
                        ເລືອກ
                      </button>
                    </div>
                    {formData.platform_icon && (
                      <div className="mt-2 p-2 border border-gray-200 rounded-md bg-gray-50">
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          ຕົວຢ່າງ:
                        </label>
                        <div className="flex items-center justify-center h-8">
                          {formData.platform_icon.startsWith("<svg") ? (
                            <div
                              className="h-6 w-6 text-gray-600"
                              dangerouslySetInnerHTML={{
                                __html: formData.platform_icon,
                              }}
                            />
                          ) : (
                            <div className="h-6 w-6 text-gray-600 flex items-center justify-center">
                              {getIconComponent(formData.platform_icon) &&
                                React.createElement(
                                  getIconComponent(formData.platform_icon),
                                  { className: "h-6 w-6" },
                                )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Profile URL
                    </label>
                    <input
                      type="url"
                      value={formData.profile_url}
                      onChange={(e) =>
                        handleInputChange("profile_url", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="ປ້ອນ URL ຂອງສື່ສັງຄົມ"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Display Name
                    </label>
                    <input
                      type="text"
                      value={formData.display_name}
                      onChange={(e) =>
                        handleInputChange("display_name", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="ປ້ອນຊື່ທີ່ສະແດງ"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Sort Order
                    </label>
                    <input
                      type="number"
                      value={formData.sort_order}
                      onChange={(e) =>
                        handleInputChange(
                          "sort_order",
                          parseInt(e.target.value) || 0,
                        )
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Active
                    </label>
                    <select
                      value={formData.is_active ? "true" : "false"}
                      onChange={(e) =>
                        handleInputChange(
                          "is_active",
                          e.target.value === "true",
                        )
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="true">ເປີດໃຊ້</option>
                      <option value="false">ປິດໃຊ້</option>
                    </select>
                  </div>
                </div>
              ) : null}
            </div>

            <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 mt-4 sm:mt-6">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingItem(null);
                  setFormData({});
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                ຍົກເລີກ
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
              >
                {saving ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Icon Selector Modal */}
      {showIconSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-2 sm:p-4 md:p-6 max-w-xs sm:max-w-2xl md:max-w-4xl w-full mx-2 sm:mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-2 sm:mb-4">
              <h3 className="text-base sm:text-lg font-medium text-gray-900">
                ເລືອກໄອຄອນ
              </h3>
              <button
                onClick={() => setShowIconSelector(false)}
                className="text-gray-400 hover:text-gray-600"
                title="ປິດ"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {iconSelectorField === "platform_icon" ? (
              <div>
                <div className="mb-2 sm:mb-4">
                  <h4 className="text-sm sm:text-md font-medium text-gray-700 mb-2">
                    ສື່ສັງຄົມ SVG Icons
                  </h4>
                  <div className="grid grid-cols-3 xs:grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2 sm:gap-3">
                    {socialMediaIcons.map((icon) => (
                      <button
                        key={icon.name}
                        onClick={() => selectIcon(icon.svg)}
                        className="flex flex-col items-center p-2 sm:p-3 border border-gray-200 rounded-lg hover:border-indigo-300 hover:bg-indigo-50 transition-colors"
                        title={icon.label}
                      >
                        <div
                          className="h-8 w-8 text-gray-600 mb-1 flex items-center justify-center"
                          dangerouslySetInnerHTML={{ __html: icon.svg }}
                        />
                        <span className="text-xs text-gray-500 text-center">
                          {icon.label}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="border-t pt-2 sm:pt-4">
                  <h4 className="text-sm sm:text-md font-medium text-gray-700 mb-2">
                    Heroicons
                  </h4>
                  <div className="grid grid-cols-3 xs:grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2 sm:gap-3">
                    {availableIcons
                      .filter(
                        (icon) =>
                          !socialMediaIcons.some(
                            (socialIcon) =>
                              socialIcon.name === icon.name.toLowerCase() ||
                              icon.name === "GlobeAltIcon" ||
                              icon.name === "ChatBubbleLeftIcon" ||
                              icon.name === "EnvelopeIcon" ||
                              icon.name === "PhoneIcon" ||
                              icon.name === "CameraIcon" ||
                              icon.name === "VideoCameraIcon" ||
                              icon.name === "HeartIcon" ||
                              icon.name === "StarIcon" ||
                              icon.name === "UserIcon" ||
                              icon.name === "UserGroupIcon",
                          ),
                      )
                      .map((icon) => (
                        <button
                          key={icon.name}
                          onClick={() => selectIcon(icon.name)}
                          className="flex flex-col items-center p-2 sm:p-3 border border-gray-200 rounded-lg hover:border-indigo-300 hover:bg-indigo-50 transition-colors"
                          title={icon.label}
                        >
                          <icon.component className="h-8 w-8 text-gray-600 mb-1" />
                          <span className="text-xs text-gray-500 text-center">
                            {icon.label}
                          </span>
                        </button>
                      ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-3 xs:grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2 sm:gap-3">
                {availableIcons.map((icon) => (
                  <button
                    key={icon.name}
                    onClick={() => selectIcon(icon.name)}
                    className="flex flex-col items-center p-2 sm:p-3 border border-gray-200 rounded-lg hover:border-indigo-300 hover:bg-indigo-50 transition-colors"
                    title={icon.label}
                  >
                    <icon.component className="h-8 w-8 text-gray-600 mb-1" />
                    <span className="text-xs text-gray-500 text-center">
                      {icon.label}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && deleteTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-4 sm:p-6 max-w-xs sm:max-w-md w-full mx-4">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
                <TrashIcon className="h-5 w-5 text-red-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-bold text-gray-900">
                  ຢືນຢັນການລຶບ
                </h3>
                <p className="mt-1 text-sm text-gray-700">
                  ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບ{" "}
                  <span className="font-semibold">
                    {deleteTarget.label || "ລາຍການນີ້"}
                  </span>
                  ?
                </p>
                <p className="text-sm text-red-600 mt-2">
                  ⚠️ ການກະທຳນີ້ບໍ່ສາມາດຍົກເລີກໄດ້.
                </p>
              </div>
            </div>
            <div className="mt-4 flex flex-col sm:flex-row-reverse gap-2">
              <button
                type="button"
                onClick={confirmDelete}
                disabled={deleting}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow px-4 py-2 bg-red-600 text-xs sm:text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleting ? "ກຳລັງລຶບ..." : "ລຶບ"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteTarget(null);
                }}
                className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow px-4 py-2 bg-white text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                ຍົກເລີກ
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hidden file input for image upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />
    </div>
  );
};

export default LandingPage;
